import { useState } from 'react';
import { api } from '../api.js';
import { decorateTeam, kr, teamHandouts, teamTypes } from '../stats.js';

const dayLabel = d => new Date(d + 'T12:00').toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' });
import { PlayerTable, SectionLabel, YearSelect } from '../components/ui.jsx';

export function Leader({ data, year, setYear, session, actions }) {
  const [playerName, setPlayerName] = useState('');
  const [busy, setBusy] = useState(false);
  const season = data.seasons[year];
  const raw = season && season.teams.find(t => t.id === session.teamId);

  const header = (
    <div className="topbar">
      <img src="/sif-logo.png" alt="SIF" />
      <div className="club">Sandareds IF</div>
      <div className="divider" />
      <div className="team-tag">{raw ? raw.name : session.name}</div>
      <div className="spacer" />
      {data.years.length > 0 && <YearSelect years={data.years} year={year} onChange={setYear} />}
      <button className="btn-ghost" onClick={actions.logout}>Logga ut</button>
    </div>
  );

  if (!raw) {
    return (
      <div>
        {header}
        <div className="page"><div className="card empty">Laget finns inte med i någon säsong ännu. Hör av dig till admin.</div></div>
      </div>
    );
  }

  const team = decorateTeam(raw, season);
  const extra = teamHandouts(season, team.id);
  const types = teamTypes(season);
  const allocText = types.map(t => `${season.allocDefaults?.[t.id] || 0} ${t.name.toLowerCase()}`).join(', ') || '–';
  const onSold = (playerId, type, v) => actions.saveSold(year, team.id, playerId, type, v);
  const onFlags = (playerId, flags) => actions.savePlayer(year, team.id, playerId, flags);
  const toggleDone = () => actions.setDone(year, team.id, !team.done);

  /* A new player gets the allocation per lottery type that admin has set for the season. */
  const addPlayer = async e => {
    e.preventDefault();
    const name = playerName.trim();
    if (!name || busy) return;
    setBusy(true);
    const saved = await actions.run(() => api.post(`/seasons/${year}/teams/${team.id}/players`, { name }),
      { ok: `${name} är tillagd.` });
    setBusy(false);
    if (saved) { actions.setTeam(year, saved); setPlayerName(''); }
  };

  const removePlayer = async p => {
    const sold = p.cells.reduce((n, c) => n + c.value, 0);
    const ok = await actions.confirm({
      title: `Ta bort ${p.name}?`,
      message: sold
        ? <>{p.name} har {sold} sålda registrerade. Spelaren och försäljningen tas bort från laget.</>
        : <>{p.name} tas bort från laget.</>,
      okLabel: 'Ta bort', danger: true,
    });
    if (!ok) return;
    const saved = await actions.run(() => api.del(`/seasons/${year}/teams/${team.id}/players/${p.id}`),
      { ok: `${p.name} är borttagen.` });
    if (saved) actions.setTeam(year, saved);
  };

  return (
    <div>
      {header}
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Lagöversikt · {year}</div>
            <h1>{team.name}</h1>
            <div className="muted" style={{ fontSize: 13.5 }}>Ledare: {team.leadersLabel}</div>
          </div>
          {team.done ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="done-pill">✓ Laget är klart</span>
              <button className="btn-ghost" style={{ padding: '9px 14px' }} onClick={toggleDone}>Ångra</button>
            </div>
          ) : (
            <button className="btn" onClick={toggleDone}>Markera laget som klart</button>
          )}
        </div>

        <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', marginBottom: 13 }}>
          <div className="kpi" style={{ padding: '16px 17px' }}>
            <div className="kpi-label" style={{ fontSize: 11.5 }}>Intjänat till SIF</div>
            <div className="kpi-value" style={{ fontSize: 24, marginTop: 7 }}>{kr(team.club)}</div>
          </div>
          <div className="kpi soft" style={{ padding: '16px 17px' }}>
            <div className="kpi-label" style={{ fontSize: 11.5 }}>Lagkassa</div>
            <div className="kpi-value" style={{ fontSize: 24, marginTop: 7 }}>{kr(team.kassa)}</div>
            <div className="kpi-note">Vinst på extraförsäljning</div>
          </div>
          <div className="kpi" style={{ padding: '16px 17px' }}>
            <div className="kpi-label" style={{ fontSize: 11.5 }}>Inbetalt från spelare</div>
            <div className="kpi-value" style={{ fontSize: 24, marginTop: 7 }}>{kr(team.paidSum)}</div>
            <div className="kpi-note muted-note">av {kr(team.toPay)} · {team.paidCount} av {team.players.length} har betalt</div>
          </div>
        </div>

        <SectionLabel>Sammanställning per lottyp</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 11 }}>
          {team.byType.map(bt => (
            <div key={bt.id} className={'type-card' + (bt.met ? ' met' : '')}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 7, minHeight: 21 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{bt.name}</div>
                {bt.met && <span className="met-badge">✓ Mål nått</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span className="mono" style={{ fontSize: 21, fontWeight: 600 }}>{bt.sold}</span>
                <span className="mono muted" style={{ fontSize: 13 }}>/ {bt.alloc} mål</span>
              </div>
              <div className="amb" style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>Returer: {bt.ret}</div>
            </div>
          ))}
        </div>

        {extra.list.length > 0 && (
          <div className="card" style={{ overflow: 'hidden', marginTop: 16 }}>
            <div className="card-head">
              <h2>Hämtade extra lotter</h2>
              <span>Utlämnat av kansliet</span>
            </div>
            <div className="list-row" style={{ borderTop: 'none', background: 'var(--gs)' }}>
              <div style={{ flex: 1, fontWeight: 700, fontSize: 14 }}>Totalt</div>
              <div className="mono" style={{ fontWeight: 700, fontSize: 14 }}>{extra.label}</div>
            </div>
            {extra.list.map(x => (
              <div key={x.id} className="list-row">
                <div className="muted" style={{ flex: 1, fontSize: 13.5 }}>{dayLabel(x.date)}</div>
                <div className="mono" style={{ fontSize: 13.5 }}>{x.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="tip">
          <span style={{ fontWeight: 800 }}>Tips:</span> När laget sålt hela sin tilldelning ({team.allocLabel}) går vinsten på alla extra lotter direkt till lagkassan. Sälj mer för att fylla kassan!
        </div>

        <h2 style={{ fontSize: 17, fontWeight: 800, margin: '24px 0 14px' }}>
          Spelare <span className="muted" style={{ fontWeight: 600 }}>({team.players.length})</span>
        </h2>
        <div className="card" style={{ overflow: 'hidden' }}>
          <form className="add-player" onSubmit={addPlayer}>
            <input className="input input-sm" value={playerName} placeholder="Ny spelare – för- och efternamn" aria-label="Ny spelares namn"
              onChange={e => setPlayerName(e.target.value)} />
            <button className="btn" type="submit" style={{ padding: '8px 14px', fontSize: 14, borderRadius: 9, whiteSpace: 'nowrap' }}
              disabled={busy || !playerName.trim()}>Lägg till spelare</button>
            <span className="muted" style={{ fontSize: 12.5 }}>Får automatiskt tilldelningen: {allocText}</span>
          </form>
          <div className="table-wrap"><PlayerTable team={team} types={types} onSold={onSold} onFlags={onFlags} onRemove={removePlayer} /></div>
        </div>
      </div>
    </div>
  );
}
